import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Pencil, Trash2, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

export default function LeaveTypesTab({ api }) {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [nameError, setNameError] = useState('');
  const [form, setForm] = useState({ name: '', color: '#71717a', xero_pay_item: '', myob_pay_item: '' });

  const fetchLeaveTypes = async () => {
    try {
      const res = await api.get('/leave-types');
      setLeaveTypes(res.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { fetchLeaveTypes(); }, []);

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    const trimmedName = form.name.trim();
    const duplicate = leaveTypes.find(
      lt => lt.name.toLowerCase() === trimmedName.toLowerCase() && lt.id !== editing?.id
    );
    if (duplicate) {
      setNameError(`A leave type named "${duplicate.name}" already exists`);
      return;
    }
    setNameError('');
    try {
      if (editing) {
        await api.put(`/leave-types/${editing.id}`, form);
        toast.success('Leave type updated');
      } else {
        await api.post('/leave-types', form);
        toast.success('Leave type created');
      }
      setDialogOpen(false);
      setEditing(null);
      setForm({ name: '', color: '#71717a', xero_pay_item: '', myob_pay_item: '' });
      setNameError('');
      fetchLeaveTypes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = (lt) => {
    setDeleteTarget(lt);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/leave-types/${deleteTarget.id}`);
      toast.success('Deleted');
      fetchLeaveTypes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleToggleActive = async (lt) => {
    try {
      await api.put(`/leave-types/${lt.id}`, { is_active: !lt.is_active });
      fetchLeaveTypes();
    } catch { toast.error('Failed to update'); }
  };

  const openEdit = (lt) => {
    setEditing(lt);
    setForm({ name: lt.name, color: lt.color, xero_pay_item: lt.xero_pay_item || '', myob_pay_item: lt.myob_pay_item || '' });
    setNameError('');
    setDialogOpen(true);
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', color: '#71717a', xero_pay_item: '', myob_pay_item: '' });
    setNameError('');
    setDialogOpen(true);
  };

  if (loading) return <div className="animate-pulse h-48 bg-muted rounded" />;

  return (
    <Card className="card-industrial">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Calendar className="h-5 w-5" /> Leave Types
          </CardTitle>
          <CardDescription>Configure leave/absence categories for worker leave management</CardDescription>
        </div>
        <Button className="btn-volt" onClick={openCreate} data-testid="add-leave-type-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Leave Type
        </Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="label-overline">Color</TableHead>
              <TableHead className="label-overline">Name</TableHead>
              <TableHead className="label-overline">Xero Pay Item</TableHead>
              <TableHead className="label-overline">MYOB Pay Item</TableHead>
              <TableHead className="label-overline">Active</TableHead>
              <TableHead className="label-overline text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {leaveTypes.map(lt => (
              <TableRow key={lt.id}>
                <TableCell>
                  <div className="w-5 h-5 rounded-full" style={{ backgroundColor: lt.color }} />
                </TableCell>
                <TableCell className="font-medium">{lt.name}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{lt.xero_pay_item || '-'}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{lt.myob_pay_item || '-'}</TableCell>
                <TableCell>
                  <Switch checked={lt.is_active} onCheckedChange={() => handleToggleActive(lt)} data-testid={`leave-type-active-${lt.id}`} />
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(lt)} data-testid={`edit-leave-type-${lt.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(lt)} className="text-red-400 hover:text-red-300" data-testid={`delete-leave-type-${lt.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {leaveTypes.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No leave types configured</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Leave Type' : 'New Leave Type'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="label-overline">Name <span className="text-red-500">*</span></Label>
              <Input value={form.name} onChange={e => { setForm({ ...form, name: e.target.value }); setNameError(''); }} placeholder="e.g. Annual Leave" data-testid="leave-type-name" />
              {nameError && <p className="text-destructive text-xs mt-1" data-testid="leave-type-name-error">{nameError}</p>}
            </div>
            <div className="space-y-2">
              <Label className="label-overline">Color</Label>
              <div className="flex items-center gap-3">
                <input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} className="w-10 h-8 rounded cursor-pointer border border-border" />
                <Input value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} className="flex-1 font-mono text-sm" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="label-overline">Xero Pay Item</Label>
                <Input value={form.xero_pay_item} onChange={e => setForm({ ...form, xero_pay_item: e.target.value })} placeholder="Optional" data-testid="leave-type-xero" />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">MYOB Pay Item</Label>
                <Input value={form.myob_pay_item} onChange={e => setForm({ ...form, myob_pay_item: e.target.value })} placeholder="Optional" data-testid="leave-type-myob" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="btn-volt" onClick={handleSubmit} data-testid="save-leave-type-btn">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-leave-type-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" />
              Delete Leave Type
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the leave type "{deleteTarget?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-leave-type-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-leave-type-button">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
